import { useMutation } from '@tanstack/react-query'

import { MutationConfig } from '@/lib/react-query'
import { supabase } from '@/lib/supabase-client'
import { logger } from '@/logger'

export interface CreateBillingPortalSessionParams {
  return_url: string
}

export interface CreateBillingPortalSessionResult {
  url: string
}

export async function createBillingPortalSession(
  params: CreateBillingPortalSessionParams
): Promise<CreateBillingPortalSessionResult> {
  const { data: session } = await supabase.auth.getSession()

  if (!session.session) {
    throw new Error('Not authenticated')
  }

  const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-billing-portal-session`

  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.session.access_token}`,
    },
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    const error = await response.json()
    logger.error('Failed to create billing portal session', { error })
    throw new Error(error.message || 'Failed to create billing portal session')
  }

  const data = await response.json()
  return data
}

type UseCreateBillingPortalSessionOptions = {
  mutationConfig?: MutationConfig<typeof createBillingPortalSession>
}

export function useCreateBillingPortalSession({
  mutationConfig,
}: UseCreateBillingPortalSessionOptions = {}) {
  return useMutation({
    mutationFn: createBillingPortalSession,
    onSuccess: (data) => {
      logger.info('Billing portal session created')
      // Redirect to Stripe billing portal
      if (data.url) {
        window.location.href = data.url
      }
    },
    onError: (error) => {
      logger.error('Failed to create billing portal session', { error })
    },
    ...mutationConfig,
  })
}
